import { api } from '@/api';
import { type RefundOffersDTO } from '@/types/dto/agent';
import { isNonEmptyString } from '@/utils/typeGuards';

const getOfferIds = (refundOffers: RefundOffersDTO['refundOffers']) =>
  (refundOffers ?? []).map((offer) => offer.id).filter(isNonEmptyString);

// The backend validates `refundToken` against the fulfillment and returns its
// refund offer (fee + refundable amount).
export const initiateRefundByToken = async ({
  bookingId,
  fulfillmentId,
  refundToken,
}: {
  bookingId: string;
  fulfillmentId: string;
  refundToken: string;
}): Promise<RefundOffersDTO> => {
  const response = await api.agentApi.RefundOffers_InitiateRefund(
    { bookingId },
    { fulfillmentIds: [fulfillmentId], refundToken }
  );

  return response.data;
};

// Releases the offer(s) without refunding, so an abandoned deep-link doesn't
// leave the fulfillment held until the offer lapses on its own.
export const deleteRefundOffers = async ({
  bookingId,
  refundOffers,
}: {
  bookingId: string;
  refundOffers: RefundOffersDTO['refundOffers'];
}): Promise<void> => {
  for (const offerId of getOfferIds(refundOffers)) {
    await api.agentApi.RefundOffers_DeleteRefund({
      bookingId,
      refundOfferId: offerId,
    });
  }
};

export const confirmRefundOffers = async ({
  bookingId,
  refundOffers,
}: {
  bookingId: string;
  refundOffers: RefundOffersDTO['refundOffers'];
}): Promise<void> => {
  const offerIds = getOfferIds(refundOffers);

  // Surface an unconfirmable refund rather than reporting a no-op as success.
  if (offerIds.length === 0) {
    throw new Error('Cannot confirm refund without a valid offer id');
  }

  for (const offerId of offerIds) {
    await api.agentApi.RefundOffers_ConfirmRefund(
      { bookingId, refundOfferId: offerId },
      { status: 'CONFIRMED' }
    );
  }
};
